import { useQuery } from '@apollo/client';
import { useFlagsmith } from 'flagsmith-react';
import {
  FindHuddlesQuery,
  FindHuddlesQueryVariables,
  FindLeadBookingsQuery,
  FindLeadBookingsQueryVariables,
  FindRequestsQuery,
  FindRequestsQueryVariables,
  HuddleStatus,
  RequestStatus,
  User,
} from 'lib/generated/graphql';
import { FIND_HUDDLES, FIND_LEAD_BOOKINGS_QUERY, FIND_REQUESTS_QUERY } from 'lib/queries';
import { useRef } from 'react';
import { MyHuddlesMap } from 'src/screens/my-projects/components/CustomerHuddlesContainer';
import CustomerCompleteProfile from 'src/screens/user/components/CustomerCompleteProfile';
import BookingCard from 'src/shared-components/BookingCard';
import RequestCard from 'src/shared-components/HuddleCard/Request';
import PageContainer from 'src/shared-components/Layout/PageContainer';
import Modal from 'src/shared-components/Modal';
import CustomerOnboardingTask from '../components/CustomerOnboardingTask';
import LeadBar from '../components/leadBar';
import CreateRequestModal from '../components/CreateRequest/Modals/CreateRequestModal';
import CreateLegacyRequestModal from "../components/CreateLegacyRequest/Modals/CreateLegacyRequestModal";

const Customer = ({ user }: { user: User }) => {
  const { hasFeature } = useFlagsmith();
  const { TeamForming, PastProject, LiveProject } = HuddleStatus;
  const { data: requestData } = useQuery<FindRequestsQuery, FindRequestsQueryVariables>(FIND_REQUESTS_QUERY, {
    variables: { where: { customerId: { equals: user?.id }, status: { not: { equals: RequestStatus.Approved } } } },
    fetchPolicy: 'cache-and-network',
  });

  const { data: bookingData } = useQuery<FindLeadBookingsQuery, FindLeadBookingsQueryVariables>(
    FIND_LEAD_BOOKINGS_QUERY,
    {
      variables: { where: { customerId: { equals: user?.id } } },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data: activeHuddleData } = useQuery<FindHuddlesQuery, FindHuddlesQueryVariables>(FIND_HUDDLES, {
    variables: {
      where: {
        request: { is: { customerId: { equals: user?.id } } },
        status: { in: [LiveProject, TeamForming] },
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: pastProjectsData } = useQuery<FindHuddlesQuery, FindHuddlesQueryVariables>(FIND_HUDDLES, {
    variables: { where: { request: { is: { customerId: { equals: user?.id } } }, status: { equals: PastProject } } },
    fetchPolicy: 'cache-and-network',
  });

  const bookings = bookingData?.findLeadBookings?.leadBookings || [];
  const requests = requestData?.findRequests?.requests;
  const activeHuddles = activeHuddleData?.findHuddles?.huddles;
  const pastHuddles = pastProjectsData?.findHuddles?.huddles || [];

  const completeProfileModalRef = useRef<any>();
  const firstRequestModalRef = useRef<any>();
  const modalCreateProjectRef = useRef<any>();
  const openProfileModal = () => completeProfileModalRef.current.open();
  const closeProfileModal = () => completeProfileModalRef.current.close();
  const openRequestModal = () => firstRequestModalRef.current.open();
  const closeRequestModal = () => firstRequestModalRef.current.close();

  const text = {
    header: 'Request your first Huddle',
    subHeader:
      "Tell the Huddle community what you're building. Focus on the goals and results you want. Like magic, you'll have a team ready to roll in 5 days or less.",
    request: 'Post a project to create your first Huddle. ',
    completeProfile: "You haven't completed your company profile yet.",
    button: 'Submit my Huddle request →',
  };

  return (
    <PageContainer
      title="My Huddles"
      sideButton={
        user?.customerOnboarding?.completedFirstRequest && (
          <div className="flex">
            <button
              color="white"
              className="font-medium border border-black py-3 px-6 rounded-full"
              onClick={() => openRequestModal()}
            >
              + Request a Huddle
            </button>
          </div>
        )
      }
    >
      {activeHuddles?.length > 0 && (
        <>
          <h1 className="font-bold uppercase mb-4">ACTIVE</h1>
          <MyHuddlesMap huddles={activeHuddles} count={activeHuddleData?.findHuddles?.count} customer={true} />
        </>
      )}
      {pastHuddles.length > 0 && (
        <>
          <h1 className="font-bold uppercase mb-4">PAST</h1>
          <MyHuddlesMap huddles={pastHuddles} count={pastProjectsData?.findHuddles?.count} />
        </>
      )}

      {!user.customerOnboarding.completedFirstRequest && (
        <div className="mt-1 mb-8 flex flex-col gap-y-4">
          <CustomerOnboardingTask text={text.request} onClick={() => openRequestModal()} />
        </div>
      )}

      {user.customerOnboarding.completedFirstRequest && user.companies[0]?.pitch === null && (
        <div className="mt-1 mb-8 flex flex-col gap-y-4">
          <CustomerOnboardingTask text={text.completeProfile} onClick={() => openProfileModal()} />
        </div>
      )}

      {requests?.length > 0 && (
        <>
          <h1 className="font-bold uppercase mb-4">PENDING REQUESTS</h1>
          <ul
            role="list"
            className="space-y-12 sm:grid sm:gap-x-6 sm:gap-y-6 sm:space-y-0 lg:grid-cols-1 lg:gap-x-6 pb-8"
          >
            {requests?.map(request => (
              <li key={request.id}>
                <RequestCard request={request} />
              </li>
            ))}
          </ul>
        </>
      )}
      {bookings?.length > 0 && (
        <div>
          <>
            <h1 className="font-bold uppercase mb-4">BOOKINGS</h1>
            <ul
              role="list"
              className="space-y-12 sm:grid sm:gap-x-6 sm:gap-y-6 sm:space-y-0 lg:grid-cols-1 lg:gap-x-6 pb-8"
            >
              {bookings?.map(leadBooking => (
                <li key={leadBooking.id}>
                  <BookingCard booking={leadBooking} />
                </li>
              ))}
            </ul>
          </>
        </div>
      )}

      {bookings?.length > 0 && <LeadBar />}
      <Modal ref={completeProfileModalRef} closeButtonForStepper maxHeight={false} closeOnBackdropClick={false}>
        <div className="max-w-2xl">
          <CustomerCompleteProfile closeModal={closeProfileModal} />
        </div>
      </Modal>
      {hasFeature('huddle_creation_request_flow_v3')
        ? <CreateRequestModal
            ref={firstRequestModalRef}
            onClose={closeRequestModal}
          />
        : <CreateLegacyRequestModal
            ref={firstRequestModalRef}
            onClose={closeRequestModal}
          />}
    </PageContainer>
  );
};

export default Customer;
